import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Rocket, Sparkles, FolderCheck, Radar, BellRing, Siren } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { JourneyTimeline, type JourneyStep } from './JourneyTimeline';
import { SAMPLE_HTTP_MONITOR } from './sample-monitor';
import { OnboardingCss } from './onboarding-css';
import { heroWrap, heroIcon, heroEyebrow, heroTitle, heroLede } from './onboarding-styles';

/**
 * State C — inside a project console with no monitors yet. Picks up the journey
 * where the project guide left off (project done → add monitor next), rendered as
 * the same hero + timeline. The monitor step opens the create wizard pre-filled
 * with an example HTTP monitor; the rest deep-link into their create flows.
 */
export function FirstRunChecklist() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const base = `/services/healthcheck/${projectId}`;

  const createBlank = () => navigate(`${base}/monitors/create`);
  // Open the create wizard pre-filled with the sample HTTP monitor (route-state).
  const createSample = () => navigate(`${base}/monitors/create`, { state: SAMPLE_HTTP_MONITOR });

  const steps: JourneyStep[] = [
    {
      title: 'Project created',
      description: 'Monitors and alarms are scoped to this project.',
      icon: <FolderCheck size={15} />,
    },
    {
      title: 'Add your first monitor',
      description: 'Start with an example HTTP monitor — just swap the URL and create.',
      icon: <Radar size={15} />,
      actionLabel: 'Add monitor',
      onAction: createSample,
    },
    {
      title: 'Connect a notification channel',
      description: 'Deliver alerts to Telegram, Email, Slack, Teams or a webhook.',
      icon: <BellRing size={15} />,
      actionLabel: 'Add channel',
      onAction: () => navigate(`${base}/notifications/create`),
    },
    {
      title: 'Set an alarm',
      description: 'Choose the failure condition that opens an incident and notifies your team.',
      icon: <Siren size={15} />,
      actionLabel: 'Create alarm',
      onAction: () => navigate(`${base}/alarms/create`),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      <OnboardingCss />

      <div className="vn-ob-hero" style={heroWrap}>
        <span className="vn-ob-halo" style={heroIcon} aria-hidden>
          <Rocket size={28} />
        </span>
        <p style={heroEyebrow}>Almost there</p>
        <h2 style={heroTitle}>Add your first monitor</h2>
        <p style={heroLede}>
          Your project is ready. Create a watchpoint to start tracking uptime and
          latency — then connect a channel and set an alarm so you never miss an incident.
        </p>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)', marginTop: 'var(--vn-space-2)', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button size="lg" startIcon={<Sparkles size={16} />} onClick={createSample}>
            Try a sample monitor
          </Button>
          <Button size="lg" variant="secondary" startIcon={<Plus size={16} />} onClick={createBlank}>
            New monitor
          </Button>
        </div>
      </div>

      <JourneyTimeline steps={steps} currentIndex={1} />
    </div>
  );
}
